import { getOwner } from '@ember/application';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import { run, next } from '@ember/runloop';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import moment from 'moment-timezone';
import rome from 'rome';
import { localCopy } from 'tracked-toolbox';

const TIME_FORMAT = 'h:mm a';

interface CalendarOptions {
  appendTo: unknown;
  autoClose: boolean;
  date: boolean;
  required: boolean;
  initialValue: moment.Moment;
  inputFormat: string;
  time: boolean;
  timeFormat: string;
  timeInterval: number;
  styles: {
    timeList: string;
  };
  max?: string | moment.Moment;
  min?: string | moment.Moment;
}

interface RomeCalendarSignature {
  Args: {
    format: string;
    max?: Date | moment.Moment;
    min?: Date | moment.Moment;
    timezone?: string;
    showDate: boolean;
    showTime: boolean;
    time: Date | null;
    timeSetter: (oldDateTime: Date | null, newDate: Date | null) => void;
    disabled?: boolean;
    isReadonly?: boolean;
    inputClass?: string;
    ariaLabel?: string;
    placeholder?: string;
    isRequired?: boolean;
    tz?: string;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: unknown;
  };
}

export default class RomeCalendar extends Component<RomeCalendarSignature> {
  elementId = guidFor(this);

  @tracked targetOpacitySelector!: string;
  @tracked calendar!: {
    destroy: () => void;
    options: (options: CalendarOptions) => void;
    on: (event: string, callback: () => void) => void;
    restore: () => void;
    destroyed: boolean;
    container: HTMLElement;
    associated: HTMLElement;
  };
  @localCopy('args.isRequired', true) isRequired!: boolean;

  get isTimepicker(): boolean {
    return !this.args.showDate && !!this.args.showTime;
  }

  @action
  onInsert(): void {
    this.targetOpacitySelector = `.rd-time-list.belongs-to-${this.elementId}`;
    this._updateCalendarOptions();
  }

  @action
  onDestroy(): void {
    this.calendar.destroy();
  }

  _updateCalendarOptions(): void {
    const calendarOptions = this._buildCalendarOptions();
    const { timeSetter, time, isReadonly } = this.args;
    let calendar = this.calendar;

    if (calendar) {
      if (calendar.destroyed) {
        calendar.restore();
      }
      calendar.options(calendarOptions);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      calendar = rome(document.querySelector(`#${this.elementId} input`)!, calendarOptions);
      this.calendar = calendar;
    }

    calendar.on('data', function () {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      // eslint-disable-next-line ember/no-runloop
      run(this, function () {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        timeSetter(time, this.getDate());
      });
    });

    calendar.on('show', () => {
      // eslint-disable-next-line ember/no-runloop
      run(this, function () {
        const { container, associated } = calendar;
        const calRect = container.getBoundingClientRect();
        const inputRect = associated.getBoundingClientRect();

        // checks if element is in viewport
        if (calRect.bottom > window.innerHeight) {
          const top = parseInt(container.style.top, 10);
          container.style.top = `${top - (calRect.height + inputRect.height)}px`;
        }
      });
    });

    if (isReadonly) {
      this.calendar.destroy();
    }
  }

  _buildCalendarOptions(): CalendarOptions {
    const isTimepicker = this.isTimepicker;
    const { tz, format, showDate, showTime } = this.args;
    const time = moment(this.args.time);
    let { max, min } = this.args;

    const rootElement = (<{ rootElement: string }>(<unknown>getOwner(this)))?.rootElement;

    const calendarOptions: CalendarOptions = {
      appendTo: document.querySelector(rootElement),
      autoClose: true,
      date: showDate,
      required: this.isRequired,
      initialValue: time,
      inputFormat: format,
      time: showTime,
      timeFormat: TIME_FORMAT,
      timeInterval: 900,
      styles: {
        timeList: `rd-time-list belongs-to-${this.elementId}`,
      },
    };

    if (isPresent(max)) {
      // passing in a timezone allows the datepicker to show the max time for a specific timezone instead of the default timezone
      max = tz ? moment.tz(max, tz) : moment(max);

      if (isTimepicker) {
        // rome doesn't always work correctly unless this matches `timeFormat`
        calendarOptions.max = max.format(TIME_FORMAT);
      } else {
        calendarOptions.max = max;
      }
    }

    if (isPresent(min)) {
      // passing in a timezone allows the datepicker to show the min time for a specific timezone instead of the default timezone
      min = tz ? moment.tz(min, tz) : moment(min);

      if (isTimepicker) {
        // rome doesn't always work correctly unless this matches `timeFormat`
        calendarOptions.min = min.format(TIME_FORMAT);
      } else {
        calendarOptions.min = min;
      }
    }

    return calendarOptions;
  }

  @action
  onClick(): void {
    // eslint-disable-next-line ember/no-runloop
    next(() => {
      this._focusOnSelectedTime();
    });
  }

  @action
  onFocusout(): void {
    // eslint-disable-next-line ember/no-runloop
    next(() => {
      this._hideListOfTimes();
    });
  }

  /*
   * Hides date drawer to prevent future flicker from selected
   * default date
   */
  _hideListOfTimes(): void {
    if (!this.args.showTime) {
      // If not showing time, do not need to focus on time
      return;
    }

    const list = document.querySelector<HTMLElement>(this.targetOpacitySelector);
    if (list && list.style) {
      list.style.opacity = '0';
    }
  }

  /*
   * Focuses/selects the currently selected time
   * Scrolls the timepicker menu to near the selected time
   * and then makes the date drawer visible to
   * prevent flicker
   */
  _focusOnSelectedTime(): void {
    if (!this.args.showTime) {
      // If not showing time, do not need to focus on time
      return;
    }

    const list = document.querySelector<HTMLElement>(this.targetOpacitySelector);
    const options = list ? [...list.querySelectorAll<HTMLElement>('.rd-time-option')] : [];
    const input = document.querySelector<HTMLInputElement>(`#${this.elementId} input`);
    const inputTime = input && input.value;

    // create a moment from the input time
    // if the input is blank then use the current time so we can still scroll to a good position
    const mInput = inputTime ? moment(inputTime, 'h:mm a') : moment();

    // round the time up to the next 15 minute interval so that we can scroll the time menu to it
    const roundedTime = mInput.minute(Math.ceil(mInput.minute() / 15) * 15).format('h:mm a');
    // get the dropdown option that is rounded up from the input time
    const matchedTimeOption = options.find((option) => option.innerText === roundedTime);
    const matchedTimeOptionIndex = options.indexOf(matchedTimeOption!);

    options.forEach((option) => option.classList.remove('selected', 'matched'));

    // scroll the timepicker menu based on where the roundedTime is in the menu
    if (matchedTimeOption) {
      // Reset scroll position of the time list
      if (list) list.scrollTop = 0;

      matchedTimeOption.classList.add('matched');
      if (inputTime === matchedTimeOption.textContent) {
        // only add the selected class if the input time matches a dropdown time
        matchedTimeOption.classList.add('selected');
      }

      if (mInput.isAfter(moment('11:45 pm', 'h:mm a'))) {
        // if we're rounding up to 12am we want to scroll to the bottom of the list instead of the top
        options[options.length - 1]?.scrollIntoView();
      } else if (matchedTimeOptionIndex < 3) {
        // if we're in the top 3 times just scroll to the first
        options[0]?.scrollIntoView();
      } else {
        // otherwise scroll to the rounded time, but offset by 3 so that we show times before and after
        options[matchedTimeOptionIndex - 3]?.scrollIntoView();
      }
    }
    // we need to set the opacity to 1 to override the css rule in the template
    if (list && list.style) {
      list.style.opacity = '1';
    }
  }

  @action
  handleKeyDown(e: KeyboardEvent): void {
    const target = e.target as HTMLInputElement;
    const isEnterKey = e.which === 13;

    if (isEnterKey) {
      e.preventDefault();
      target.blur();
    }
  }

  @action
  handleInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    if (target.value === '') {
      this.args.timeSetter(null, null);
    }
  }

  <template>
    <div
      {{didInsert this.onInsert}}
      {{willDestroy this.onDestroy}}
      {{didUpdate this.onInsert @time}}
      {{on "click" this.onClick}}
      {{on "focusout" this.onFocusout}}
      class="entry-time-input {{if @showTime 'show-time'}}"
      id={{this.elementId}}
      ...attributes
    >
      {{! template-lint-disable no-forbidden-elements }}
      <style>
        {{this.targetOpacitySelector}}
        { opacity: 0; }
      </style>
      <input
        aria-label={{@ariaLabel}}
        autocapitalize="off"
        autocomplete="rome-calendar-input"
        class={{concat @inputClass (if @isReadonly " readonly")}}
        disabled={{@disabled}}
        {{on "input" this.handleInput}}
        {{on "keypress" this.handleKeyDown}}
        placeholder={{@placeholder}}
        readonly={{@isReadonly}}
        spellcheck="false"
        type="text"
      />
      <div class="romeContainer" style={{if @isReadonly "display: none;"}} />
      {{yield}}
    </div>
  </template>
}
